import { AccountSettingsPanel } from '../account-settings-panel';
import { Trans } from '@ui/i18n/trans';
import { AccountSettingsId } from '@common/auth/ui/account-settings/account-settings-sidenav';
import {
  ActiveSession,
  useUserSessions,
} from '@common/auth/ui/account-settings/sessions-panel/requests/use-user-sessions';
import { ComputerIcon } from '@ui/icons/material/Computer';
import { SmartphoneIcon } from '@ui/icons/material/Smartphone';
import { TabletIcon } from '@ui/icons/material/Tablet';
import { FormattedRelativeTime } from '@ui/i18n/formatted-relative-time';
import { SvgIconProps } from '@ui/icons/svg-icon';
import { Fragment, ReactNode } from 'react';
import { ProgressCircle } from '@ui/progress/progress-circle';
import clsx from 'clsx';
import { useLogoutOtherSessions } from '@common/auth/ui/account-settings/sessions-panel/requests/use-logout-other-sessions';
import { usePasswordConfirmedAction } from '@common/auth/ui/confirm-password/use-password-confirmed-action';
import { Button } from '@ui/buttons/button';
import { toast } from '@ui/toast/toast';
import { message } from '@ui/i18n/message';

export function SessionsPanel() {
  const { data, isLoading } = useUserSessions();
  const logoutOther = useLogoutOtherSessions();
  const { withConfirmedPassword, isLoading: checkingPasswordStatus } =
    usePasswordConfirmedAction({ needsPassword: true });

  const sessionList = (
    <div className="space-y-12">
      {data?.sessions?.map(session => (
        <SessionItem key={session.id} session={session} />
      ))}
    </div>
  );

  return (
    <AccountSettingsPanel
      id={AccountSettingsId.Sessions}
      title={<Trans message="Active sessions" />}
    >
      <div className="mb-24 text-sm text-white/60 leading-relaxed">
        <Trans message="If necessary, you may log out of all of your other browser sessions across all of your devices. Your recent sessions are listed below. If you feel your account has been compromised, you should also update your password." />
      </div>
      <div className="my-30">
        {isLoading ? (
          <div className="min-h-60">
            <ProgressCircle isIndeterminate />
          </div>
        ) : (
          sessionList
        )}
      </div>
      <Button
        variant="flat"
        className="bg-white/5 font-semibold text-white/90 transition-all hover:bg-white/10"
        disabled={checkingPasswordStatus || logoutOther.isPending}
        onClick={() => {
          withConfirmedPassword(password => {
            logoutOther.mutate(
              { password: password! },
              {
                onSuccess: () => {
                  toast(message('Logged out other sessions.'));
                },
              },
            );
          });
        }}
      >
        <Trans message="Logout Other Sessions" />
      </Button>
    </AccountSettingsPanel>
  );
}

interface SessionItemProps {
  session: ActiveSession;
}
function SessionItem({ session }: SessionItemProps) {
  return (
    <div className={clsx(
      "flex items-center gap-20 p-16 rounded border transition-colors",
      session.is_current_device ? "bg-primary/5 border-primary/20" : "bg-white/[0.02] border-white/5"
    )}>
      <div className={clsx(
        "flex h-48 w-48 items-center justify-center rounded",
        session.is_current_device ? "bg-primary text-white" : "bg-white/5 text-white/40"
      )}>
        <DeviceIcon device={session.device_type} size="md" />
      </div>
      <div className="flex-auto">
        <div className="font-semibold text-white/90">
          <ValueOrUnknown>{session.platform}</ValueOrUnknown> •{' '}
          <ValueOrUnknown>{session.browser}</ValueOrUnknown>
        </div>
        <div className="text-sm text-white/50">
          {session.city}, {session.country} • <IpAddress session={session} />
        </div>
      </div>
      <div className="text-right text-xs font-medium uppercase tracking-wider">
        <LastActive session={session} />
      </div>
    </div>
  );
}

interface DeviceIconProps {
  device: ActiveSession['device_type'];
  size: SvgIconProps['size'];
}
function DeviceIcon({ device, size }: DeviceIconProps) {
  switch (device) {
    case 'mobile':
      return <SmartphoneIcon size={size} />;
    case 'tablet':
      return <TabletIcon size={size} />;
    default:
      return <ComputerIcon size={size} />;
  }
}

interface LastActiveProps {
  session: ActiveSession;
}
function LastActive({ session }: LastActiveProps) {
  if (session.is_current_device) {
    return (
      <span className="text-positive">
        <Trans message="This device" />
      </span>
    );
  }

  return <FormattedRelativeTime date={session.last_active} />;
}

interface IpAddressProps {
  session: ActiveSession;
}
function IpAddress({ session }: IpAddressProps) {
  if (session.ip_address) {
    return <span>{session.ip_address}</span>;
  } else if (session.token) {
    return <Trans message="API Token" />;
  }
  return <Trans message="Unknown IP" />;
}

interface ValueOrUnknownProps {
  children: ReactNode;
}
function ValueOrUnknown({ children }: ValueOrUnknownProps) {
  return children ? (
    <Fragment>{children}</Fragment>
  ) : (
    <Trans message="Unknown" />
  );
}
